import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import TcnApp from '../../../components/tcn/TcnApp';
import TopBar from '../../../components/tcn/layout/TopBar';
import { Card, Icon, AccountAvatar } from '../../../components/tcn/ui-kit';
import AlertBell from '../../../components/tcn-modules/m7/AlertBell';
import { TCN } from '../../../components/tcn/ui-kit/tokens';
import CreatorsSectionTabs from '../../../components/tcn-modules/shared/CreatorsSectionTabs';
import SectionHeader from '../../../components/tcn-modules/shared/SectionHeader';
import OnboardingHeaderStrip from '../../../components/tcn-modules/m2/OnboardingHeaderStrip';
import CreatorBucket from '../../../components/tcn-modules/m2/CreatorBucket';
import MilestoneTracker from '../../../components/tcn-modules/m2/MilestoneTracker';
import PipelineView from '../../../components/tcn-modules/m2/PipelineView';
import ProspectFormModal from '../../../components/tcn-modules/m2/ProspectFormModal';
import ActionPlanModal from '../../../components/tcn-modules/m4/ActionPlanModal';
import useIsDesktop from '../../../hooks/useIsDesktop';

/**
 * /tcn/me/new-creators — Module 2 (My New Creator).
 *
 * One scrollable screen, sub-views stacked as big-title blocks (audit §0.1):
 *   [Onboarding] tab → A2.1 list (+ A2.2 sort) → A2.3 T+14 → A2.4 T+30 → A2.5 Graduation.
 *   [Pipeline]   tab → A2.7 manual recruitment pipeline (Phase 1).
 * A2.6 action plan = modal. Reached via the shared [Creators | New creators] segment.
 */
const isAtRisk = (c) =>
  c.t14_status === 'At risk' ||
  c.t14_status === 'Missed' ||
  c.t30_status === 'At risk' ||
  c.t30_status === 'Missed' ||
  c.graduation_status === 'At risk';

const isCritical = (c) =>
  c.t14_status === 'Missed' || c.t30_status === 'Missed';

const isPassed = (c) =>
  c.t14_status === 'Passed' && (c.t30_status === 'Passed' || !c.t30_status);

// A2.6 — short banner reason for the action sheet, derived from the milestone
// the creator is missing (most severe first). Uses clear fields only.
const milestoneReason = (c, t) => {
  if (c.t14_status === 'Missed' || c.t14_status === 'At risk') {
    return t('tcn.m2.reason.t14', '{{status}} T+14 · {{n}}/5 LIVE days', {
      status: t(`tcn.m2.status.${(c.t14_status || '').replace(/\s+/g, '_').toLowerCase()}`, c.t14_status),
      n: c.live_days_in_first_14 ?? 0,
    });
  }
  if (c.t30_status === 'Missed' || c.t30_status === 'At risk') {
    return t('tcn.m2.reason.t30', '{{status}} T+30 · {{d}}/15 days · {{h}}/45h', {
      status: t(`tcn.m2.status.${(c.t30_status || '').replace(/\s+/g, '_').toLowerCase()}`, c.t30_status),
      d: c.live_days_in_first_30 ?? 0,
      h: typeof c.live_hours_in_first_30 === 'number' ? Math.round(c.live_hours_in_first_30) : 0,
    });
  }
  if (c.graduation_status === 'At risk' || c.graduation_status === 'Not on track') {
    return t('tcn.m2.reason.grad', 'Graduation {{status}}', {
      status: t(`tcn.m2.status.${(c.graduation_status || '').replace(/\s+/g, '_').toLowerCase()}`, c.graduation_status),
    });
  }
  return undefined; // fall back to alert_status in the modal
};

const milestoneTone = (c) => {
  if (c.t14_status === 'Missed' || c.t30_status === 'Missed') return 'red';
  if (c.graduation_status === 'Not on track') return 'red';
  return 'amber';
};

// A2.2 — sort the onboarding cohort by proximity to next milestone, tenure, or risk.
const RISK_RANK = (c) => (isCritical(c) ? 3 : isAtRisk(c) ? 2 : isPassed(c) ? 0 : 1);
const sortCreators = (list, sort) => {
  const rows = [...list];
  if (sort === 'days') {
    rows.sort((a, b) => (b.days_since_joining ?? 0) - (a.days_since_joining ?? 0));
  } else if (sort === 'risk') {
    rows.sort((a, b) => RISK_RANK(b) - RISK_RANK(a));
  } else {
    // 'deadline' (default) — closest next milestone first, nulls last.
    rows.sort((a, b) => {
      const da = a.next_milestone_date ? new Date(a.next_milestone_date).getTime() : Infinity;
      const db = b.next_milestone_date ? new Date(b.next_milestone_date).getTime() : Infinity;
      return da - db;
    });
  }
  return rows;
};

const TcnNewCreators = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const isDesktop = useIsDesktop();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialGap = searchParams.get('gap');
  const view = searchParams.get('view') === 'pipeline' ? 'pipeline' : 'onboarding';
  const sort = ['deadline', 'days', 'risk'].includes(searchParams.get('sort'))
    ? searchParams.get('sort')
    : 'deadline';

  const userName = userProfile?.full_name || userProfile?.display_name || userProfile?.email || 'Agent';

  const [creators, setCreators] = useState([]);
  const [gradDiamonds, setGradDiamonds] = useState({}); // creatorId → decrypted diamonds_mtd (A2.5 ring)
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [actionModalCreator, setActionModalCreator] = useState(null);
  const [prospects, setProspects] = useState([]);
  const [editingProspect, setEditingProspect] = useState(null); // null=closed, {}=new, row=edit

  const setView = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'pipeline') params.set('view', 'pipeline');
    else params.delete('view');
    setSearchParams(params, { replace: true });
  };

  const setSort = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === 'deadline') params.delete('sort');
    else params.set('sort', next);
    setSearchParams(params, { replace: true });
  };

  const loadProspects = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data, error } = await supabase
        .from('recruitment_pipeline')
        .select(
          'id, tiktok_username, tiktok_link, status, source, forecast_onboard_month, contacted_at, replied, last_follow_up_date, support_needed'
        )
        .eq('agent_id', user.id)
        .order('updated_at', { ascending: false });
      setProspects(error ? [] : data || []);
    } catch {
      setProspects([]);
    }
  }, [user?.id]);

  const load = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data: cc } = await supabase
      .from('creator_current_month')
      .select('*')
      .eq('manager_id', user.id)
      .neq('status', 'departed')
      .lte('days_since_joining', 30)
      .order('next_milestone_date', { ascending: true, nullsFirst: false });
    setCreators(cc || []);
    setLoading(false);
    loadProspects();
  }, [user?.id, loadProspects]);

  useEffect(() => {
    load();
  }, [load]);

  // A2.5 — bulk-decrypt the new creators' MTD diamonds for the Graduation rings.
  // R2 blinds the SUPER ADMIN to per-creator diamonds; the agent IS authorised
  // to see their own roster, so we decrypt this small onboarding subset (≤30)
  // via tcn-decrypt-creator (creatorIds[]) → ring = diamonds_mtd / grad target.
  useEffect(() => {
    let cancelled = false;
    const ids = creators.map((c) => c.creator_id).filter(Boolean);
    if (ids.length === 0) { setGradDiamonds({}); return; }
    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('tcn-decrypt-creator', {
          body: { creatorIds: ids },
        });
        if (cancelled || error || !data) return;
        const map = {};
        for (const [cid, vals] of Object.entries(data)) {
          if (vals && typeof vals.diamonds_mtd === 'number') map[cid] = vals.diamonds_mtd;
        }
        setGradDiamonds(map);
      } catch {
        /* keep encrypted fallback ("Diamonds on profile") */
      }
    })();
    return () => { cancelled = true; };
  }, [creators]);

  // Pre-filter to gap-relevant creators if `gap` query param present
  const filtered = useMemo(() => {
    if (!initialGap) return creators;
    if (initialGap === 't14_ratio') {
      return creators.filter((c) => c.t14_status === 'At risk' || c.t14_status === 'Missed');
    }
    if (initialGap === 'go_live_rate_15d_45h_first30') {
      return creators.filter((c) => c.t30_status === 'At risk' || c.t30_status === 'Missed');
    }
    return creators;
  }, [creators, initialGap]);

  const sorted = useMemo(() => sortCreators(filtered, sort), [filtered, sort]);

  // Bucket the creators (A2.1), preserving the chosen sort order within buckets.
  const buckets = useMemo(() => {
    const critical = [];
    const at_risk = [];
    const on_track = [];
    const passed = [];
    for (const c of sorted) {
      if (isCritical(c)) critical.push(c);
      else if (isAtRisk(c)) at_risk.push(c);
      else if (isPassed(c)) passed.push(c);
      else on_track.push(c);
    }
    return { critical, at_risk, on_track, passed };
  }, [sorted]);

  const handleTabChange = (tab) => {
    switch (tab) {
      case 'today':    return navigate('/tcn/me');
      case 'creators': return navigate('/tcn/me/creators');
      case 'tiers':    return navigate('/tcn/me/tiers');
      case 'payout':   return navigate('/tcn/me/payout');
      case 'actions':  return navigate('/tcn/me/actions');
      default:         return navigate('/tcn/me');
    }
  };

  const handleSubmitActionPlan = async (payload) => {
    if (!actionModalCreator || !user?.id) return;
    await supabase.from('action_log').insert({
      tenant_id: actionModalCreator.tenant_id,
      agency_id: actionModalCreator.agency_id,
      agent_id: user.id,
      ...payload,
      source_activity: 'new_creator_at_risk',
      source_entity_type: 'creator',
      source_entity_id: actionModalCreator.creator_id,
    });
  };

  // A2.7 — pipeline CRUD. tenant/agency derived from the profile (RLS with-check
  // requires agency_id = jwt_agency_id() and agent_id = auth.uid()).
  const tenantId = userProfile?.tenant_id || creators[0]?.tenant_id;
  const agencyId = userProfile?.agency_id || creators[0]?.agency_id;

  const handleSaveProspect = async (payload) => {
    if (!user?.id) throw new Error('Not signed in');
    if (editingProspect?.id) {
      const { error } = await supabase
        .from('recruitment_pipeline')
        .update(payload)
        .eq('id', editingProspect.id);
      if (error) throw error;
    } else {
      const { error } = await supabase.from('recruitment_pipeline').insert({
        tenant_id: tenantId,
        agency_id: agencyId,
        agent_id: user.id,
        ...payload,
      });
      if (error) throw error;
    }
    await loadProspects();
  };

  const handleDeleteProspect = async (id) => {
    const { error } = await supabase.from('recruitment_pipeline').delete().eq('id', id);
    if (error) throw error;
    await loadProspects();
  };

  return (
    <TcnApp
      tab="creators"
      onTabChange={handleTabChange}
      desktopBreadcrumb={[
        t('tcn.tabs.creators', 'Creators'),
        t('tcn.m2.title', 'New creators'),
      ]}
      mobileHeader={
        <TopBar
          leading={<AccountAvatar name={userName} size={36} ring />}
          trailing={<AlertBell />}
          title={t('tcn.m2.title', 'New creators')}
          subtitle={t('tcn.m2.subtitle', 'Onboarding tracker')}
          largeTitle
        />
      }
    >
      <div
        style={{
          padding: isDesktop ? 0 : '0 14px',
          maxWidth: isDesktop ? 1200 : '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {/* Entry segment [Creators | New creators] (audit §0.2) */}
        <CreatorsSectionTabs active="new" isDesktop={isDesktop} />

        {/* Onboarding ↔ Pipeline (A2.7) */}
        <div
          style={{
            display: 'flex',
            gap: 4,
            padding: 4,
            background: TCN.surface1,
            border: `1px solid ${TCN.border}`,
            borderRadius: 12,
            alignSelf: isDesktop ? 'flex-start' : 'stretch',
          }}
        >
          {[
            { key: 'onboarding', label: t('tcn.m2.tab.onboarding', 'Onboarding') },
            { key: 'pipeline', label: t('tcn.m2.tab.pipeline', 'Pipeline') },
          ].map((seg) => {
            const active = view === seg.key;
            return (
              <button
                key={seg.key}
                type="button"
                onClick={() => setView(seg.key)}
                style={{
                  flex: isDesktop ? '0 0 auto' : 1,
                  minWidth: isDesktop ? 120 : undefined,
                  padding: '8px 14px',
                  borderRadius: 9,
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 13,
                  fontWeight: 600,
                  background: active ? TCN.surface3 : 'transparent',
                  color: active ? TCN.text : TCN.textDim,
                }}
              >
                {seg.label}
              </button>
            );
          })}
        </div>

        {view === 'pipeline' ? (
          loading ? (
            <div style={{ height: 120, background: TCN.surface1, borderRadius: 12 }} className="animate-pulse" />
          ) : (
            <PipelineView
              prospects={prospects}
              isDesktop={isDesktop}
              onAdd={() => setEditingProspect({})}
              onEdit={(p) => setEditingProspect(p)}
            />
          )
        ) : loading ? (
          <div style={{ height: 200, background: TCN.surface1, borderRadius: 12 }} className="animate-pulse" />
        ) : filtered.length === 0 ? (
          <Card>
            <div style={{ textAlign: 'center', padding: 24 }}>
              <Icon name="people" size={32} color={TCN.textMute} />
              <div style={{ marginTop: 10, fontSize: 14, color: TCN.text, fontWeight: 600 }}>
                {t('tcn.m2.empty.title', 'No new creators in the last 30 days')}
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: TCN.textDim }}>
                {t('tcn.m2.empty.body', 'Onboarding tracker is empty. Recruit a creator to start.')}
              </div>
            </div>
          </Card>
        ) : (
          <>
            {/* A2.1 + A2.2 — Onboarding list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionHeader
                title={t('tcn.m2.onboarding.title', 'Onboarding')}
                subtitle={t('tcn.m2.onboarding.subtitle', '{{count}} creators in first 30 days', {
                  count: filtered.length,
                })}
              />
              <OnboardingHeaderStrip
                buckets={{
                  critical: buckets.critical.length,
                  at_risk: buckets.at_risk.length,
                  on_track: buckets.on_track.length,
                  passed: buckets.passed.length,
                }}
                isDesktop={isDesktop}
              />
              {/* A2.2 — sort control */}
              <div
                style={{
                  display: 'flex',
                  gap: 4,
                  padding: 4,
                  background: TCN.surface1,
                  border: `1px solid ${TCN.border}`,
                  borderRadius: 10,
                }}
              >
                {[
                  { id: 'deadline', label: t('tcn.m2.sort.deadline', 'Deadline') },
                  { id: 'days', label: t('tcn.m2.sort.days', 'Days joined') },
                  { id: 'risk', label: t('tcn.m2.sort.risk', 'Risk') },
                ].map((s) => {
                  const active = sort === s.id;
                  return (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSort(s.id)}
                      style={{
                        flex: 1,
                        padding: '7px 0',
                        borderRadius: 7,
                        border: 'none',
                        cursor: 'pointer',
                        fontFamily: 'inherit',
                        fontSize: 12,
                        fontWeight: 600,
                        background: active ? TCN.surface3 : 'transparent',
                        color: active ? TCN.text : TCN.textDim,
                      }}
                    >
                      {s.label}
                    </button>
                  );
                })}
              </div>
              <div>
                <CreatorBucket kind="critical" creators={buckets.critical} expandedId={expandedId} onExpand={setExpandedId} onCreateActionPlan={setActionModalCreator} />
                <CreatorBucket kind="at_risk" creators={buckets.at_risk} expandedId={expandedId} onExpand={setExpandedId} onCreateActionPlan={setActionModalCreator} />
                <CreatorBucket kind="on_track" creators={buckets.on_track} expandedId={expandedId} onExpand={setExpandedId} onCreateActionPlan={setActionModalCreator} />
                <CreatorBucket kind="passed" creators={buckets.passed} expandedId={expandedId} onExpand={setExpandedId} onCreateActionPlan={setActionModalCreator} />
              </div>
            </div>

            {/* A2.3 — T+14 tracker */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionHeader
                title={t('tcn.m2.t14.title', 'T+14 tracker')}
                subtitle={t('tcn.m2.t14.subtitle', '≥5 valid LIVE days in first 14d')}
              />
              <MilestoneTracker view="t14" creators={sorted} />
            </div>

            {/* A2.4 — T+30 tracker */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionHeader
                title={t('tcn.m2.t30.title', 'T+30 tracker')}
                subtitle={t('tcn.m2.t30.subtitle', '≥15 LIVE days · ≥45 hours in 30d')}
              />
              <MilestoneTracker view="t30" creators={sorted} />
            </div>

            {/* A2.5 — Graduation */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <SectionHeader
                title={t('tcn.m2.grad.title', 'Graduation')}
                subtitle={t('tcn.m2.grad.subtitle', 'Path to Diamond')}
              />
              <MilestoneTracker view="grad" creators={sorted} decDiamonds={gradDiamonds} />
            </div>
          </>
        )}
      </div>

      <ActionPlanModal
        open={!!actionModalCreator}
        onClose={() => setActionModalCreator(null)}
        onSubmit={handleSubmitActionPlan}
        creator={actionModalCreator}
        sourceActivity="new_creator_at_risk"
        contextLabel={actionModalCreator ? milestoneReason(actionModalCreator, t) : undefined}
        contextTone={actionModalCreator ? milestoneTone(actionModalCreator) : undefined}
      />

      <ProspectFormModal
        open={editingProspect !== null}
        prospect={editingProspect}
        onClose={() => setEditingProspect(null)}
        onSave={handleSaveProspect}
        onDelete={handleDeleteProspect}
      />
    </TcnApp>
  );
};

export default TcnNewCreators;
